import type { ReactNode } from "react";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  ActivityToolThumbnail,
  ContentToolThumbnail,
} from "../tools/tool-thumbnail";
import type { HistoryEntry } from "./history-model";

export function HistoryEntryThumbnail({ entry }: { entry: HistoryEntry }) {
  if (entry.group === "activity" && entry.activityToolId) {
    return <ActivityToolThumbnail id={entry.activityToolId} />;
  }

  switch (entry.contentToolId) {
    case "note":
      return <NoteContentThumbnail content={entry.noteContent ?? ""} />;
    case "moment":
      return <MomentContentThumbnail imageUrl={entry.thumbnailUrl} />;
    case "swap":
      return (
        <SwapContentThumbnail
          mentorImageUrl={entry.thumbnailUrl}
          menteeImageUrl={entry.secondaryThumbnailUrl}
        />
      );
    case null:
      return null;
    default: {
      const _exhaustive: never = entry.contentToolId;
      return _exhaustive;
    }
  }
}

function NoteContentThumbnail({ content }: { content: string }) {
  const trimmed = content.replace(/\s+/g, " ").trim();
  return (
    <ArtBox>
      <div className="relative flex h-[34px] w-[24px] -rotate-[4deg] flex-col overflow-hidden rounded-[1.5px] border-[0.5px] border-[#efece6] bg-[#f7f5f1] p-[3px] shadow-[0px_0.3px_1.3px_0px_rgba(0,0,0,0.08)]">
        <div className="absolute left-[2px] top-[2px] size-[3px] rounded-full bg-stone-300" />
        {trimmed ? (
          <p className="mt-[5px] line-clamp-6 overflow-hidden font-mono text-[3.5px] leading-[1.2] text-text-primary">
            {trimmed}
          </p>
        ) : (
          <div className="mt-[5px] flex flex-col gap-[1px]">
            {NOTE_PLACEHOLDER_WIDTHS.map((width, i) => (
              <div
                key={i}
                style={{ width }}
                className="h-[1px] rounded-full bg-stone-300/70"
              />
            ))}
          </div>
        )}
      </div>
    </ArtBox>
  );
}

function MomentContentThumbnail({ imageUrl }: { imageUrl: string | null }) {
  const { url: signedUrl } = useSignedMedia(imageUrl);
  if (!signedUrl) {
    return <ContentToolThumbnail id="moment" />;
  }
  return (
    <ArtBox>
      <div className="absolute left-[8px] top-[3px] h-[30px] w-[21px] -rotate-[6.5deg] rounded-[1.5px] border-[0.5px] border-[#e2ddd4] bg-[#ece8e1]" />
      <div className="absolute left-[7px] top-[3px] h-[30px] w-[21px] rotate-[9deg] overflow-hidden rounded-[1.5px] border-[0.5px] border-[#e2ddd4] bg-[#ece8e1]">
        <img src={signedUrl} alt="" className="h-full w-full object-cover" />
      </div>
    </ArtBox>
  );
}

/**
 * Mirrors Hub `SwapCard`: mentee on the back (or transparent), mentor on the
 * front (or transparent). Missing side stays a frosted empty card — photos
 * only appear for the side that has actually been submitted.
 */
function SwapContentThumbnail({
  mentorImageUrl,
  menteeImageUrl,
}: {
  mentorImageUrl: string | null;
  menteeImageUrl: string | null;
}) {
  const { url: mentorSignedUrl } = useSignedMedia(mentorImageUrl);
  const { url: menteeSignedUrl } = useSignedMedia(menteeImageUrl);

  return (
    <ArtBox>
      <SwapSlot
        className="left-[4px] top-[2px] -rotate-[13.85deg]"
        src={menteeSignedUrl}
      />
      <SwapSlot
        className="left-[10px] top-[5px] rotate-[3.98deg]"
        src={mentorSignedUrl}
      />
    </ArtBox>
  );
}

function SwapSlot({
  className,
  src,
}: {
  className: string;
  src: string | null | undefined;
}) {
  if (src) {
    return (
      <div
        className={`absolute h-[26px] w-[18px] overflow-hidden rounded-[1.5px] border-[0.5px] border-white bg-white shadow-[0px_0.3px_2.6px_0px_rgba(0,0,0,0.06)] ${className}`}
      >
        <img src={src} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }

  // Frosted empty card — same “transparent thingy” as Tools swap art / Hub pending side.
  return (
    <div
      className={`absolute h-[26px] w-[18px] overflow-hidden rounded-[1.5px] border-[0.5px] border-white bg-white/50 shadow-[0px_0.3px_2.6px_0px_rgba(0,0,0,0.06)] backdrop-blur-[0.5px] ${className}`}
    >
      <div className="absolute left-[1.5px] top-[1.5px] size-[3.5px] rounded-full bg-zinc-300/90" />
    </div>
  );
}

const NOTE_PLACEHOLDER_WIDTHS = [
  "100%",
  "100%",
  "80%",
  "100%",
  "100%",
  "60%",
] as const;

function ArtBox({ children }: { children: ReactNode }) {
  return (
    <div className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden">
      {children}
    </div>
  );
}
